import React from 'react'
import parse, { domToReact } from 'html-react-parser'
import HPSupportSpecificLink from '../components/HPSupportSpecificLink'

const builtInReplacerMap = {
  HP_SUPPORT_PRODUCT_LINK: HPSupportSpecificLink
}

/**
 * Resolves a single data-replacer node into a React element.
 * Supports both built-in replacers owned by this file and caller-provided replacements.
 *
 * @param {Object} params
 * @param {Object} params.domNode - The parsed DOM node being inspected.
 * @param {Object} params.parserOptions - html-react-parser options used to render nested children.
 * @param {Object.<string, React.ReactNode>} [params.replacements={}] - Caller-provided replacer mappings.
 * @returns {React.ReactNode|undefined} A replacement node, or undefined when the DOM node should be left unchanged.
 */
export const resolveReplacerTag = ({
  domNode,
  parserOptions,
  replacements = {}
}) => {
  if (!(domNode.type === 'tag' && domNode?.attribs?.['data-replacer'])) {
    return undefined
  }

  const replacerId = domNode.attribs['data-replacer']
  const BuiltInReplacer = builtInReplacerMap[replacerId]

  if (BuiltInReplacer) {
    return (
      <BuiltInReplacer>
        {domToReact(domNode.children || [], parserOptions)}
      </BuiltInReplacer>
    )
  }

  if (Object.prototype.hasOwnProperty.call(replacements, replacerId)) {
    return replacements[replacerId]
  }

  return undefined
}

/**
 * Receives an HTML string and looks for elements with the attribute data-replacer="...".
 * Replaces each found element with either a built-in React component or the corresponding React content
 * from the replacements map.
 *
 * @param {string} htmlString - The HTML string to be parsed.
 * @param {Object.<string, React.ReactNode>} replacements - An object where the keys are the data-replacer values
 * and the values are the React nodes to be inserted.
 * @returns {React.ReactElement|null} The parsed React nodes.
 */
export const renderReplacerTags = (htmlString, replacements) => {
  if (!htmlString || !replacements) {
    return null
  }

  const parserOptions = {
    replace: (domNode) =>
      resolveReplacerTag({ domNode, parserOptions, replacements })
  }

  return parse(htmlString, parserOptions)
}
